import { Toaster } from "@/components/ui/toaster";
import { useState, useEffect } from "react";
import { CategoryFilter } from "@/components/CategoryFilter";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { MainContent } from "@/components/home/MainContent";
import { useArticles } from "@/hooks/useArticles";
import { useSearchParams } from "react-router-dom";
import { Moon, Sun } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/hooks/useTheme";
import logo from "../../public/lovable-uploads/3fbbe33e-16b8-4ec5-8434-2b85d4531768.png";
import { useLogos } from "@/hooks/useLogos";

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    searchParams.get("category")
  );
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();
  const { getLogoUrl } = useLogos();

  const { articles, totalPages, isLoading } = useArticles(
    selectedCategory,
    selectedSubcategoryId,
    currentPage
  );

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("name")
        .order("name");

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de charger les catégories",
          variant: "destructive",
        });
        return;
      }

      setCategories(data.map((cat) => cat.name));
    };

    fetchCategories();
  }, [toast]);

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    setSelectedSubcategoryId(null);
    setCurrentPage(1);
    
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  const handleSubcategorySelect = (subcategoryId: string) => {
    setSelectedSubcategoryId(subcategoryId);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-background text-foreground"
    >
      <header className="bg-[#35009B]">
        <div className="container mx-auto flex items-center justify-between h-[72px] px-4">
          <div className="flex-1 flex justify-center">
            <img 
              src={getLogoUrl('main_logo')}
              alt="Les Fous du Volant Logo" 
              className="h-[2.3rem] w-auto cursor-pointer"
              onClick={() => handleCategorySelect(null)}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="absolute right-4 hidden md:inline-flex rounded-full hover:bg-accent text-white"
          >
            <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Changer le thème</span>
          </Button>
        </div>
      </header>

      <nav className="bg-magazine-red text-white sticky top-0 z-50">
        <div className="container mx-auto">
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={handleCategorySelect}
            onSelectSubcategory={handleSubcategorySelect}
          />
        </div>
      </nav>

      <main>
        <MainContent
          selectedCategory={selectedCategory}
          articles={articles}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </main>

      <Toaster />
    </motion.div>
  );
};

export default Home;
